import {pick} from 'lodash';
import Request from '@/library/http/Request';

export default class Model {
    /**
     * Returns the name of the property that holds the primary key.
     */
    static get primaryKey() {
        return 'id';
    }

    /**
     * The api endpoint of the model.
     */
    static get endpoint() {
        return;
    }

    /**
     * Returns the value on the primary key.
     */
    get primaryKey() {
        return this[this.constructor.primaryKey];
    }

    /**
     * Sets the value on the primary key.
     */
    set primaryKey(primaryKey) {
        this[this.constructor.primaryKey] = primaryKey;
    }

    /**
     * The api endpoint of the instantiated model.
     */
    get endpoint() {
        return `${this.constructor.endpoint}/${this.primaryKey}`;
    }

    /**
     * The fields that can be filled of the instantiated model.
     */
    get fillables() {
        return Object.keys(this);
    }

    /**
     * The Model constructor.
     */
    constructor(data = {}) {
        this.fill(data);
    }

    /**
     * Collects an array with values into an array with models.
     */
    static collect(values) {
        let collected = values;

        if (!values) {
            collected = [];
        }

        return collected.map((value) => {
            return new this(value);
        });
    }

    /**
     * Creates an instance of Request.
     */
    static createRequest(options = {}) {
        return new Request(options);
    }

    /**
     * Returns an instance of Request that will execute a GET request and
     * handle the response as a single instance.
     */
    static find(primaryKey, options = {}) {
        const model = new this();

        model.primaryKey = primaryKey;

        return this.createRequest({
            url: model.endpoint,
            transformResponse: [(json) => {
                return new this(JSON.parse(json));
            }],
        });
    }

    /**
     * Returns an instance of Request that will execute a GET request and
     * handle the response as array.
     */
    static get(options = {}) {
        options.url = options.url || this.endpoint;

        return this.createRequest({
            ...options,
            transformResponse: [(json) => {
                return this.collect(JSON.parse(json));
            }],
        });
    }

    /**
     * Returns an instance of Request that will execute a GET request and
     * handle the response as paginated.
     */
    static paginate(page = 1, options = {}) {
        options.url = options.url || this.endpoint;

        options.params = {
            ...(options.params || {}),
            page,
        };

        return this.createRequest({
            ...options,
            transformResponse: [(json) => {
                let pagination = JSON.parse(json);

                pagination.data = this.collect(pagination.data);

                return pagination;
            }],
        });
    }

    /**
     * Returns an instance of Request that will execute a DELETE request
     * to the instantiated model's endpoint.
     */
    delete(options = {}) {
        options.url = options.url || this.endpoint;

        options.method = options.method || 'delete';

        return this.constructor.createRequest({
            ...options,
        });
    }

    /**
     * Fills the model with properties passed through data.
     */
    fill(data = {}) {
        Object.assign(this, data);
    }

    /**
     * Returns an instance of Request that will execute a POST or PATCH request
     * depending whether the primary key is present or not. The Request data is filtered
     * so that only 'fillable' fields are sent.
     */
    save(options = {}) {
        let url = this.constructor.endpoint;

        let method = 'post';

        if (this.primaryKey !== undefined) {
            url = this.endpoint;

            method = 'patch';
        }

        options.url = options.url || url;

        options.method = options.method || method;

        options.data = pick(this, this.fillables);

        return this.constructor.createRequest({
            ...options,
            transformResponse: [(json) => {
                let data = JSON.parse(json);

                if (data.message && data.errors) {
                    return data;
                }

                return new this.constructor(data);
            }],
        });
    }
}
